import React, {
  FC,
  useEffect,
  useState,
  useMemo,
  useCallback,
  forwardRef,
} from 'react';

import { Reply } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  useTheme,
  Box,
  Chip,
  Stack,
  Collapse,
  Pagination,
  TextField,
  Typography,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import { get, uniqBy } from 'lodash';
import { MentionsInput, Mention } from 'react-mentions';
import shallow from 'zustand/shallow';

import { EProposalStatus } from '../../interfaces/proposal';
import { useAuthStore } from '../../stores/auth';
import { useSingleProposalStore } from '../../stores/singleProposal';
import { chunkArray } from '../../utils/chunkArray';
import { abbreviateId } from '../../utils/humanizer';
import { Card } from '../atoms/Card';
import { ThreadedCommentShape, ThreadedComment, ParentComment } from '../molecules/comment';
import defaultDarkStyle from '../molecules/comment/mentionsDarkStyle';
import defaultStyle from '../molecules/comment/mentionsDefaultStyle';

enum CommentSortDirection {
  Descending = 'Latest',
  Ascending = 'Oldest',
}

interface MentionsData {
  id: number;
  display: string;
}[];

interface ProposalCommentsProps {
  comments: {
    userTag?: string; // will be tagged if user is a team member
    date: Date;
    text: string;
    publicAddress: string;
    threadedComments?: Array<ThreadedCommentShape> | [];
    pgx?: number;
    _id?: string;
  }[];
}

interface RenderCommentBoxProps {
  isReply: boolean,
  disabled: boolean,
  placeholder: string;
  value: string;
  onChange: (event: never) => void;
  onAdd: (address: string, isAdd: boolean) => void;
  parentCommentId?: string | '';
}

interface MentionsLogicProps {
  onChange: (value: string) => void;
  onAdd: (address: string, isAdd: boolean) => void;
  value: string;
  data: Array<MentionsData>;
}

const TextMaskCustom = forwardRef<HTMLElement, MentionsLogicProps>(
  function TextMaskCustom(props, ref) {
    const { onChange, value, data, onAdd, ...other } = props;
    const theme = useTheme();
    const themeMode = get(theme, ['palette', 'mode'], 'light');

    return (
      <>
        <MentionsInput
          {...other}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          inputRef={ref}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          singleLine={false}
          style={themeMode === 'dark' ? defaultDarkStyle : defaultStyle}
          a11ySuggestionsListLabel="Suggested user wallets"
        >
          <Mention
            trigger="@"
            data={data}
            markup="[__display__]"
            style={{
              backgroundColor: blueGrey[themeMode === 'dark' ? 800 : 50],
              color: 'transparent',
            }}
            appendSpaceOnAdd
            onAdd={(_id, display) => onAdd(display, true)}
          />
        </MentionsInput>
      </>
    );
  },
);

export const ProposalComments: FC<ProposalCommentsProps> = ({ comments }) => {
  const storedToken = useAuthStore((store) => store.storedToken);
  const { proposal, loading, error, commentSingleProposal } = useSingleProposalStore(
    ({ proposal, loading, error, commentSingleProposal }) => ({
      proposal,
      loading,
      error,
      commentSingleProposal,
    }),
    shallow,
  );

  const { _id, status: proposalStatus } = proposal || {};

  const [replyInputMentions, setReplyInputMentions] = useState<Array<string>>([]);
  const [commentInputMentions, setCommentInputMentions] = useState<Array<string>>([]);
  const [comment, setComment] = useState<string>('');
  const [reply, setReply] = useState<string>('');
  const [targetThread, setTargetThread] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>(CommentSortDirection.Ascending);
  const [commentPage, setCommentPage] = useState<number>(1);

  const handleReplyInputMentions = (address: string, isAdd: boolean) => {
    if (isAdd) {
      setReplyInputMentions((prevState) => {
        if (!prevState.find((a) => a === address)) {
          return [...prevState, address];
        }
        return prevState;
      });
    } else {
      setReplyInputMentions((prevState) => {
        return prevState.filter(prev => prev !== address);
      });
      setReply((prevState) => prevState.replaceAll(address, '').replaceAll('[] ', ''));
    }
  };
  const handleCommentInputMentions = (address: string, isAdd: boolean) => {
    if (isAdd) {
      setCommentInputMentions((prevState) => {
        if (!prevState.find((a) => a === address)) {
          return [...prevState, address];
        }
        return prevState;
      });
    } else {
      setCommentInputMentions((prevState) => {
        return prevState.filter(prev => prev !== address);
      });
      setComment((prevState) => prevState.replaceAll(address, '').replaceAll('[] ', ''));
    }
  };

  const handleChangeComment = (value: string) => {
    if (!value) setCommentInputMentions([]);
    else {
      commentInputMentions.forEach((mention) => {
        if (!value.includes(mention)) {
          handleCommentInputMentions(mention, false);
        }
      })
    }
    setComment(value);
  };
  const handleChangeReply = (value: string) => {
    if (!value) setReplyInputMentions([]);
    else {
      replyInputMentions.forEach((mention) => {
        if (!value.includes(mention)) {
          handleReplyInputMentions(mention, false);
        }
      })
    }
    setReply(value)
  };
  const handleChangeCommentOrder = (event: React.MouseEvent<HTMLElement>, newOrder: string) => {
    setSortBy(newOrder);
  };

  const handleComment = useCallback((parentCommentId: string | '' | undefined) => {
    const payload = {
      accessToken: storedToken,
      proposalId: _id,
      comment: parentCommentId ? reply : comment,
    };
    commentSingleProposal(
      parentCommentId
        ? { ...payload, parentCommentId } // is reply
        : payload
    );
  }, [reply, comment, replyInputMentions, commentInputMentions, storedToken, _id]);

  const handleCommentPageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCommentPage(value);
  };

  const showReplyToThread = (targetThreadId: string, autoMentionedAddr: string) => {
    if (targetThreadId === targetThread) {
      // closes the thread
      setTargetThread('');
      setReply('');
    } else {
      setTargetThread(targetThreadId);
      setReply(`[${autoMentionedAddr}] `);
      setReplyInputMentions([autoMentionedAddr]);
    }
  };

  useEffect(
    function clearComment() {
      if (!loading && !error) {
        setComment('');
        setReply('');
        setCommentInputMentions([]);
        setReplyInputMentions([]);
        setTargetThread('');
      }
    },
    [loading, error],
  );

  const commentTooShort = comment.length < 10;
  const commentTooLong = comment.length > 300;
  const replyTooShort = reply.length < 10;
  const replyTooLong = reply.length > 300;

  //Comments are sort by Ascending by default
  // If set to Descending, sort the other way round
  const sortedComments = useMemo(() => (
    sortBy === CommentSortDirection.Ascending
      ? [...comments].map((comment) => ({
        ...comment,
        threadedComments: comment.threadedComments && comment.threadedComments.length > 0
          ? comment.threadedComments.sort(function (a, b) {
            return a.date > b.date ? 1 : a.date < b.date ? -1 : 0;
          })
          : []
      }))
      : [...comments].map((comment) => ({
          ...comment,
          threadedComments: comment.threadedComments && comment.threadedComments.length > 0
            ? comment.threadedComments.sort(function (a, b) {
              return a.date < b.date ? 1 : a.date > b.date ? -1 : 0;
            })
            : []
        })).sort(function (a, b) {
          return a.date < b.date ? 1 : a.date > b.date ? -1 : 0;
        })
    ), [comments, sortBy]);

  const commentWallets = useMemo(() => (
    uniqBy(sortedComments.map(({ publicAddress, userTag }, index) => ({
      id: index,
      display: userTag || publicAddress,
    })), 'display')
  ), [sortedComments]);

  const chunkedComments = chunkArray(sortedComments, 10);

  const renderCommentBox = ({
    isReply,
    disabled,
    placeholder,
    value,
    onAdd,
    onChange,
    parentCommentId,
  }: RenderCommentBoxProps) => (
    <>
      <TextField
        color="primary"
        fullWidth
        disabled={disabled}
        id="comment"
        name="comment"
        label={`${placeholder} (10 characters required)`}
        value={value}
        onChange={onChange}
        multiline
        rows={4}
        sx={{
          mb: 1,
          boxShadow: 8,
          borderRadius: 1,
          backgroundColor: 'background.default',
        }}
        inputProps={{
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          onChange,
          onAdd,
          data: commentWallets,
          value,
          isReply,
          spellCheck: false,
        }}
        InputProps={{
          inputComponent: TextMaskCustom as never,
          endAdornment: (
            <InputAdornment position="end" sx={{ alignItems: 'start' }}>
              <LoadingButton
                variant="contained"
                onClick={() => handleComment(parentCommentId)}
                disabled={isReply
                  ? (replyTooShort || replyTooLong)
                  : (commentTooShort || commentTooLong)
                }
                loading={loading}
              >
                {isReply ? 'Reply' : 'Comment'}
              </LoadingButton>
            </InputAdornment>
          ),
        }}
      />
      {commentTooLong && (
        <Typography color="error" sx={{ ml: 1 }}>
          Limit of 300 characters reached
        </Typography>
      )}
    </>
  );

  return (
    <Card
      titleBoxColor="yellow"
      title="Comments"
      titleBoxText={`${comments.length}`}
      sx={{ mt: 3 }}
      endAdornment={
        <ToggleButtonGroup color="standard" value={sortBy} exclusive onChange={handleChangeCommentOrder} size="small">
          {Object.values(CommentSortDirection).map((status) => (
            <ToggleButton value={status}>{status}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      }
    >
      <Box
        sx={{
          '& > *:not(:last-child)': {
            borderBottom: 1,
            borderColor: 'divider',
            pb: 4,
          },
        }}
      >
        {chunkedComments[commentPage - 1].map((comment) => (
          <Box sx={{ mb: 3 }}>
            <ParentComment
              {...comment}
              showReplyToThread={showReplyToThread}
              proposalStatus={proposalStatus}
            />
            {comment.threadedComments.length > 0 && (
              <Box mt={1}>
                {comment.threadedComments.map((reply: ThreadedCommentShape) => (
                  <ThreadedComment
                    {...reply}
                    isReplyBoxOpen={targetThread === comment._id}
                  />
                ))}
              </Box>
            )}
            <Collapse in={targetThread === comment._id}>
              <Stack
                direction="row"
                alignItems="center"
                sx={{ mt: 2 }}
              >
                <Reply color="primary" sx={{ transform: 'rotate(180deg)', mr: 1 }} />
                <Stack
                  direction="column"
                  alignItems="start"
                  sx={{ width: '100%' }}
                >
                  {renderCommentBox({
                    isReply: true,
                    disabled: loading || proposal?.status === EProposalStatus.Closed,
                    placeholder: 'Write a reply to this thread',
                    value: reply,
                    onChange: handleChangeReply,
                    onAdd: handleReplyInputMentions,
                    parentCommentId: comment._id,
                  })}
                  {replyInputMentions.length > 0 && (
                    <Stack direction="row">
                      {replyInputMentions.map((addr) => (
                        <Chip
                          key={addr}
                          label={abbreviateId(addr)}
                          onDelete={() => handleReplyInputMentions(addr, false)}
                        />
                      ))}
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </Collapse>
          </Box>
        ))}
      </Box>

      <Box sx={{ mt: 8, mb: 2, pt: 4, borderTop: '1px solid' }}>
        {renderCommentBox({
          isReply: false,
          disabled: loading || proposal?.status === EProposalStatus.Closed,
          placeholder: 'Write a new comment',
          value: comment,
          onChange: handleChangeComment,
          onAdd: handleCommentInputMentions,
        })}
        {commentInputMentions.length > 0 && (
          <Stack direction="row">
            {commentInputMentions.map((addr) => (
              <Chip
                key={addr}
                label={abbreviateId(addr)}
                onDelete={() => handleCommentInputMentions(addr, false)}
              />
            ))}
          </Stack>
        )}
      </Box>

      {chunkedComments.length > 1 && (
        <Box sx={{ mb: 3, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Pagination
            color="primary"
            size="large"
            count={chunkedComments.length} // Page Count
            page={commentPage} // Current Page
            onChange={handleCommentPageChange}
          />
        </Box>
      )}
    </Card>
  );
};
